import React, { useContext } from 'react';
import { Text } from '@airwallex/typography';
import { Tag, TagVariantProp } from '@airwallex/tag';
import { HStack } from '@airwallex/stacks';
import { InvoicePaymentStatus } from '@/constants';
import { useTranslation } from 'react-i18next';
import { StoreContext } from '@/providers/storeProvider';
import { useTabletOrMobile } from '@/hooks/useMediaQuery';
import { CreditStatus, RefundStatus } from '@/components/Invoice/types';
import * as colors from '@airwallex/colors';

type StatusMapType<ValueType> = {
  variant?: TagVariantProp;
  color?: string;
  text: string;
  value: ValueType;
};

export const useRefundStatusMap: () => Record<
  RefundStatus | CreditStatus,
  StatusMapType<RefundStatus | CreditStatus>
> = () => {
  const { t } = useTranslation();
  return {
    PARTIALLY_REFUNDED: {
      color: colors.blues[50],
      text: t('partiallyRefunded'),
      value: 'PARTIALLY_REFUNDED',
    },
    FULLY_REFUNDED: {
      color: colors.greens[50],
      text: t('fullyRefunded'),
      value: 'FULLY_REFUNDED',
    },
    PARTIALLY_CREDITED: {
      color: colors.blues[50],
      text: t('partiallyCredited'),
      value: 'PARTIALLY_CREDITED',
    },
    FULLY_CREDITED: {
      color: colors.greens[50],
      text: t('fullyCredited'),
      value: 'FULLY_CREDITED',
    },
  };
};

export const useInvoicePaymentStatusMap: () => Record<
  InvoicePaymentStatus,
  StatusMapType<InvoicePaymentStatus>
> = () => {
  const { t } = useTranslation();
  return {
    PAID: {
      variant: 'SUCCESS',
      text: t('invoice.paid'),
      value: InvoicePaymentStatus.PAID,
    },
    UNPAID: {
      variant: 'WARNING',
      text: t('invoice.unpaid'),
      value: InvoicePaymentStatus.UNPAID,
    },
  };
};

const Metadata: React.FC = () => {
  const isTabletOrMobile = useTabletOrMobile();
  const invoiceStatusMap = useInvoicePaymentStatusMap();
  const refundStatusMap = useRefundStatusMap();
  const { t } = useTranslation();
  const { invoiceData } = useContext(StoreContext);
  const { invoice, legalEntity, theme } = invoiceData;
  const { paymentStatus, refundStatus, creditStatus } = invoice;
  const { name: legalEntityName } = legalEntity || {};
  return (
    <HStack style={{ justifyContent: 'space-between', flexWrap: 'wrap-reverse', rowGap: '8px' }}>
      <HStack space="4px" style={{ alignItems: 'flex-start' }}>
        <Text variant="headline300">{t('summary')}</Text>
        <Tag variant={invoiceStatusMap[paymentStatus]?.variant} style={{ marginRight: 4 }}>
          {invoiceStatusMap[paymentStatus]?.text}
        </Tag>
        <HStack space="4px" style={{ alignItems: 'center' }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 8 8" fill="none">
            <circle
              cx="4"
              cy="4"
              r="4"
              fill={
                refundStatus
                  ? refundStatusMap[refundStatus]?.color
                  : creditStatus
                  ? refundStatusMap[creditStatus]?.color
                  : ''
              }
            />
          </svg>
          <Text variant="body">
            {refundStatus
              ? refundStatusMap[refundStatus]?.text
              : creditStatus
              ? refundStatusMap[creditStatus]?.text
              : ''}
          </Text>
        </HStack>
      </HStack>
      <HStack
        space="8px"
        align="CENTER"
        style={{
          alignItems: 'center',
          height: '24px',
          flexBasis: isTabletOrMobile ? '100%' : 'auto',
          justifyContent: isTabletOrMobile ? 'flex-start' : 'flex-end',
        }}
      >
        {theme?.logo ? (
          <img
            id="logo"
            src={theme.logo}
            alt={legalEntity?.name || 'logo'}
            style={{
              maxHeight: '24px',
              width: 'auto',
              height: 'auto',
              display: 'block',
            }}
          />
        ) : (
          <Text variant="headline300">{legalEntityName}</Text>
        )}
      </HStack>
    </HStack>
  );
};

export default Metadata;
